(() => {
  if (window.LumiRecentlyViewed) {
    window.LumiRecentlyViewed.initialize(document);
    return;
  }

  const STORAGE_KEY = 'lumi:recently-viewed:v1';
  const STORAGE_VERSION = 1;
  const MAX_AGE = 90 * 24 * 60 * 60 * 1000;
  const controllers = new Set();
  const controllerByRoot = new WeakMap();

  const toPositiveInteger = (value, fallback) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  };

  const storageCandidate = () => {
    for (const name of ['localStorage', 'sessionStorage']) {
      try {
        const storage = window[name];
        const testKey = `${STORAGE_KEY}:test`;
        storage.setItem(testKey, '1');
        storage.removeItem(testKey);
        return storage;
      } catch (_error) {
        // Try the next browser storage option.
      }
    }
    return null;
  };

  const identityFor = (entry) => String(entry?.id || entry?.handle || '').trim();

  const normalizeHistory = (value, limit) => {
    const source = Array.isArray(value) ? value : value?.items;
    if (!Array.isArray(source)) return [];

    const now = Date.now();
    const seen = new Set();

    return source.reduce((items, entry) => {
      const id = String(entry?.id || '').trim();
      const handle = String(entry?.handle || '').trim();
      const viewedAt = Number(entry?.viewedAt || now);
      const identity = id || handle;

      if (!identity || !handle || seen.has(identity) || now - viewedAt > MAX_AGE) return items;
      seen.add(identity);
      items.push({ id, handle, viewedAt });
      return items;
    }, []).slice(0, limit);
  };

  const readHistory = (storage, limit) => {
    if (!storage) return [];
    try {
      return normalizeHistory(JSON.parse(storage.getItem(STORAGE_KEY) || 'null'), limit);
    } catch (_error) {
      return [];
    }
  };

  const writeHistory = (storage, items, limit) => {
    if (!storage) return false;
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify({
        version: STORAGE_VERSION,
        items: normalizeHistory(items, limit)
      }));
      return true;
    } catch (_error) {
      return false;
    }
  };

  const formatWithDelimiters = (cents, precision = 2, thousands = ',', decimal = '.') => {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '0';

    const fixed = (amount / 100).toFixed(precision);
    const [integer, fraction] = fixed.split('.');
    const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  };

  const moneyTokenValue = (cents, token) => {
    const formats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ',']
    };
    const [precision, thousands, decimal] = formats[token] || formats.amount;
    return formatWithDelimiters(cents, precision, thousands, decimal);
  };

  const stripMarkup = (value) => {
    const decoder = document.createElement('textarea');
    decoder.innerHTML = String(value || '').replace(/<[^>]*>/g, '');
    return decoder.value;
  };

  class RecentlyViewedController {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('[data-recently-viewed-grid]');
      this.count = root.querySelector('[data-recently-viewed-count]');
      this.empty = root.querySelector('[data-recently-viewed-empty]');
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.storage = storageCandidate();
      this.productsToShow = Math.min(4, toPositiveInteger(root.dataset.productsToShow, 4));
      this.historyLimit = toPositiveInteger(root.dataset.historyLimit, 12);
      this.requestVersion = 0;
      this.current = this.readCurrentProduct();
      this.bind();
      this.refresh({ recordCurrent: true });
    }

    readCurrentProduct() {
      const node = this.root.querySelector('[data-recently-viewed-product]');
      if (!node) return null;
      try {
        const value = JSON.parse(node.textContent);
        const id = String(value?.id || '').trim();
        const handle = String(value?.handle || '').trim();
        return id && handle ? { id, handle } : null;
      } catch (_error) {
        return null;
      }
    }

    bind() {
      window.addEventListener('storage', (event) => {
        if (event.key === STORAGE_KEY) this.refresh({ recordCurrent: false });
      }, { signal: this.signal });
    }

    async refresh({ recordCurrent }) {
      if (!this.current || !this.grid || !this.storage) {
        this.showEmpty();
        return;
      }

      const history = readHistory(this.storage, this.historyLimit);
      const currentIdentity = identityFor(this.current);
      const previous = history.filter((entry) => identityFor(entry) !== currentIdentity);

      if (recordCurrent) {
        writeHistory(this.storage, [
          { ...this.current, viewedAt: Date.now() },
          ...previous
        ], this.historyLimit);
      }

      if (!previous.length) {
        this.showEmpty();
        return;
      }

      const requestVersion = ++this.requestVersion;
      this.root.hidden = false;
      this.root.setAttribute('aria-busy', 'true');
      if (this.empty) this.empty.hidden = true;

      const seen = new Set();
      const products = [];
      let cursor = 0;

      while (cursor < previous.length && products.length < this.productsToShow) {
        const remaining = this.productsToShow - products.length;
        const batch = previous.slice(cursor, cursor + remaining);
        cursor += batch.length;

        const results = await Promise.allSettled(
          batch.map((entry) => this.fetchProduct(entry.handle))
        );
        if (requestVersion !== this.requestVersion || this.signal.aborted) return;

        results.forEach((result) => {
          if (result.status !== 'fulfilled' || products.length >= this.productsToShow) return;
          const candidate = result.value;
          const identity = String(candidate?.id || candidate?.handle || '');
          if (!identity || identity === currentIdentity || seen.has(identity)) return;
          seen.add(identity);
          products.push(candidate);
        });
      }

      if (!products.length) {
        this.showEmpty();
        return;
      }

      this.grid.replaceChildren(...products.map((product) => this.createCard(product)));
      if (this.count) this.count.textContent = String(products.length).padStart(2, '0');
      this.root.hidden = false;
      this.root.setAttribute('aria-busy', 'false');
      if (this.empty) this.empty.hidden = true;
      this.notifyItemsUpdated();
    }

    async fetchProduct(handle) {
      const rootRoute = (this.root.dataset.routesRoot || '/').replace(/\/?$/, '/');
      const url = new URL(`${rootRoute}products/${encodeURIComponent(handle)}.js`, window.location.origin);
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: this.signal
      });
      if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
      return response.json();
    }

    productUrl(handle) {
      const rootRoute = (this.root.dataset.routesRoot || '/').replace(/\/?$/, '/');
      return `${rootRoute}products/${encodeURIComponent(handle)}`;
    }

    imageUrl(product) {
      const featured = product?.featured_image || product?.images?.[0];
      const source = typeof featured === 'string' ? featured : featured?.src;
      if (!source) return '';
      try {
        const normalized = source.startsWith('//') ? `${window.location.protocol}${source}` : source;
        const url = new URL(normalized, window.location.origin);
        url.searchParams.set('width', '1000');
        return url.toString();
      } catch (_error) {
        return source;
      }
    }

    formatMoney(cents) {
      const template = this.root.dataset.moneyFormat || '';
      const match = template.match(/\{\{\s*(\w+)\s*\}\}/);
      if (match) {
        return stripMarkup(template.replace(match[0], moneyTokenValue(cents, match[1])));
      }

      try {
        return new Intl.NumberFormat(this.root.dataset.locale || document.documentElement.lang, {
          style: 'currency',
          currency: this.root.dataset.currency || 'USD'
        }).format(Number(cents) / 100);
      } catch (_error) {
        return formatWithDelimiters(cents);
      }
    }

    createCard(product) {
      const card = document.createElement('article');
      const url = this.productUrl(product.handle);
      const available = Boolean(product.available);
      const title = String(product.title || product.handle || 'Product');
      card.className = 'recommendation-product-card';
      card.dataset.productCardId = String(product.id || product.handle);
      card.setAttribute('role', 'listitem');

      const visual = document.createElement('div');
      visual.className = 'recommendation-product-card__visual';

      const media = document.createElement('a');
      media.className = 'recommendation-product-card__media';
      media.dataset.imageRatio = 'compact';
      media.href = url;
      media.setAttribute('aria-label', title);

      const imageSource = this.imageUrl(product);
      if (imageSource) {
        const image = document.createElement('img');
        image.src = imageSource;
        image.alt = title;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.sizes = '(min-width: 1200px) 22vw, (min-width: 750px) 44vw, 78vw';
        media.append(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'recommendation-product-card__placeholder recommendation-product-card__placeholder--empty';
        placeholder.setAttribute('aria-hidden', 'true');
        media.append(placeholder);
      }

      visual.append(media);

      const content = document.createElement('div');
      content.className = 'recommendation-product-card__content';

      const heading = document.createElement('h3');
      const titleLink = document.createElement('a');
      heading.className = 'recommendation-product-card__title';
      titleLink.href = url;
      titleLink.textContent = title;
      heading.append(titleLink);
      content.append(heading);

      const price = Number(product.price ?? product.price_min ?? product.variants?.[0]?.price ?? 0);
      const compareAt = Number(
        product.compare_at_price_max
        ?? product.compare_at_price
        ?? product.variants?.[0]?.compare_at_price
        ?? 0
      );
      const priceLine = document.createElement('div');
      const currentPrice = document.createElement('span');
      priceLine.className = 'product-price recommendation-product-card__price';
      currentPrice.className = 'product-price__current';
      currentPrice.textContent = this.formatMoney(price);
      priceLine.append(currentPrice);

      if (compareAt > price) {
        const comparePrice = document.createElement('s');
        comparePrice.className = 'product-price__compare';
        comparePrice.textContent = this.formatMoney(compareAt);
        priceLine.append(comparePrice);
      }
      content.append(priceLine);

      const variants = Array.isArray(product.variants) ? product.variants : [];
      const defaultVariant = variants.length === 1 ? variants[0] : null;
      const hasOnlyDefaultVariant = Boolean(
        defaultVariant
        && (
          product.has_only_default_variant === true
          || String(defaultVariant.title || '').trim().toLowerCase() === 'default title'
        )
      );
      const canQuickAdd = (
        this.root.dataset.showQuickAdd === 'true'
        && available
        && hasOnlyDefaultVariant
        && defaultVariant?.available
        && defaultVariant?.id
      );

      if (canQuickAdd) {
        const form = document.createElement('form');
        const variantInput = document.createElement('input');
        const quantityInput = document.createElement('input');
        const button = document.createElement('button');
        const idleLabel = `${this.root.dataset.quickAddLabel || 'Add to cart'} - ${title}`;
        const successLabel = `Added to cart - ${title}`;
        const errorLabel = `Unable to add. Try again - ${title}`;

        form.className = 'recommendation-product-card__quick-add';
        form.method = 'post';
        form.action = `${(this.root.dataset.routesRoot || '/').replace(/\/?$/, '/')}cart/add`;
        variantInput.type = 'hidden';
        variantInput.name = 'id';
        variantInput.value = String(defaultVariant.id);
        quantityInput.type = 'hidden';
        quantityInput.name = 'quantity';
        quantityInput.value = '1';
        button.className = 'recommendation-product-card__quick-add-button';
        button.type = 'submit';
        button.name = 'add';
        button.dataset.state = 'idle';
        button.dataset.idleLabel = idleLabel;
        button.dataset.successLabel = successLabel;
        button.dataset.errorLabel = errorLabel;
        button.setAttribute('aria-label', idleLabel);
        button.innerHTML = `
          <span class="recommendation-product-card__quick-add-icon recommendation-product-card__quick-add-icon--cart" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M6.75 8.25h10.5L18 19.5H6L6.75 8.25Z"></path>
              <path d="M9 9V6.75a3 3 0 0 1 6 0V9M12 11.5v5M9.5 14h5"></path>
            </svg>
          </span>
          <span class="recommendation-product-card__quick-add-icon recommendation-product-card__quick-add-icon--loading" aria-hidden="true"></span>
          <span class="recommendation-product-card__quick-add-icon recommendation-product-card__quick-add-icon--success" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none"><path d="m6.75 12.5 3.3 3.3 7.2-7.6"></path></svg>
          </span>
          <span class="recommendation-product-card__quick-add-icon recommendation-product-card__quick-add-icon--error" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none"><path d="M12 7.5v6M12 17.25v.05"></path></svg>
          </span>
          <span class="visually-hidden" role="status" aria-live="polite" data-quick-add-status></span>
        `;
        const status = button.querySelector('[data-quick-add-status]');
        if (status) status.textContent = this.root.dataset.quickAddLabel || 'Add to cart';
        form.append(variantInput, quantityInput, button);
        visual.append(form);
      }

      card.append(visual, content);
      return card;
    }

    showEmpty() {
      this.requestVersion += 1;
      this.grid?.replaceChildren();
      if (this.count) this.count.textContent = '00';
      this.root.setAttribute('aria-busy', 'false');

      if (this.empty) {
        this.empty.hidden = false;
        this.root.hidden = false;
      } else {
        this.root.hidden = true;
      }

      this.notifyItemsUpdated();
    }

    notifyItemsUpdated() {
      this.root.dispatchEvent(new CustomEvent('recommendation:items-updated'));
    }

    destroy() {
      this.requestVersion += 1;
      this.abortController.abort();
      controllers.delete(this);
      controllerByRoot.delete(this.root);
    }
  }

  const cleanup = () => {
    controllers.forEach((controller) => {
      if (!controller.root.isConnected) controller.destroy();
    });
  };

  const initialize = (scope = document) => {
    cleanup();
    const roots = [];
    if (scope instanceof Element && scope.matches('[data-recently-viewed-root]')) roots.push(scope);
    if (scope instanceof Document || scope instanceof DocumentFragment || scope instanceof Element) {
      roots.push(...scope.querySelectorAll('[data-recently-viewed-root]'));
    }

    roots.forEach((root) => {
      if (controllerByRoot.has(root)) return;
      const controller = new RecentlyViewedController(root);
      controllerByRoot.set(root, controller);
      controllers.add(controller);
    });
  };

  window.LumiRecentlyViewed = { initialize };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    controllers.forEach((controller) => {
      if (event.target === controller.root || event.target.contains(controller.root)) controller.destroy();
    });
  });
})();
